import {
  User,
  UserManager,
  WebStorageStateStore,
  type UserManagerSettings,
} from "oidc-client-ts";
import _ from "lodash";
import axios from "axios";
import { useProfileStore, UserProfile } from "@/stores/profile";
import * as constants from "@/constants";
import type { IConfigState } from "@/stores/config";

export class AuthService {
  private readonly _userManager: UserManager;

  constructor(config: IConfigState) {
    const options: UserManagerSettings = {
      authority: config.identity.authority,
      client_id: config.identity.MDMFrontendClientId,
      redirect_uri: `${window.location.origin}${constants.OIDC_CALLBACK_URI}`,
      response_type: constants.OIDC_RESPONSE_TYPE,
      scope: constants.OIDC_SCOPE,
      automaticSilentRenew: true,
      monitorSession: false,
      silentRequestTimeoutInSeconds: 10,
      accessTokenExpiringNotificationTimeInSeconds: 60,
      userStore: new WebStorageStateStore({
        store: window.sessionStorage,
      }),
    };
    this._userManager = new UserManager({ ...options });

    this._userManager.events.addUserLoaded((user) => {
      _.isFunction(this.userLoaded) && this.userLoaded(user);
    });

    this._userManager.events.addSilentRenewError(async () => {
      const user = await this._userManager.getUser();
      if (user !== null && user.expired) {
        console.log(
          "The token renewal failed because it had expired. This can occur when the computer falls asleep for an extended period of time!"
        );
        window.location.reload();
      } else {
        console.log("The token renewal failed, might need to add scope offline_access in IDP!");
      }
    });

    this._userManager.events.removeUserLoaded(() => {
      _.isFunction(this.userRemoved) && this.userRemoved();
    });

    //when it's authenticated and endUser reloads the page,
    //these code will init profiflestore data and set axios header
    this._userManager.getUser().then((user) => {
      // eslint-disable-next-line no-empty
      if (!_.isNil(user)) {
      }
      _.isFunction(this.userLoaded) && !_.isNil(user) && this.userLoaded?.(user as User);
    });
  }

  private async userLoaded(user: User) {
    this.setAxiosHeaderToken(user);
    const profileStore = useProfileStore();
    profileStore.setProfile({
      authenticated: true,
      user: new UserProfile(user),
      _accessibleCommand: null,
      _accessControlPromise: null,
    });
    //set access control
    await profileStore.setAccessControl();
  }

  private setAxiosHeaderToken(user: User) {
    axios.defaults.headers.common.Authorization = `Bearer ${user.access_token}`;
  }

  private userRemoved() {
    delete axios.defaults.headers.common.Authorization;
    const profileStore = useProfileStore();
    profileStore.setAuthenticatedFalse();
  }

  public getUser(): Promise<User | null> {
    return this._userManager.getUser();
  }

  public async isLoggedIn(): Promise<boolean> {
    const user = await this.getUser();
    return user !== null && !user.expired;
  }

  public signIn(returnToUrl = ""): Promise<void> {
    return returnToUrl
      ? this._userManager.signinRedirect({ state: returnToUrl })
      : this._userManager.signinRedirect();
  }

  public signInRedirectCallback(): Promise<User> {
    return this._userManager.signinRedirectCallback();
  }

  public signOut(): Promise<void> {
    return this._userManager.signoutRedirect();
  }
}
